import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Calendar,
  User,
  CreditCard,
  Clock,
  Home,
  Users,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Edit3,
  Save,
  X,
} from 'lucide-react';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import { ACTUALIZAR_RESERVA } from '../../graphql/reservas';
import { GET_HABITACIONES } from '../../graphql/habitaciones';
import './ReservaDetalleModal.css';

const toDateInput = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.split('T')[0];
  return new Date(d).toLocaleDateString('en-CA');
};

const calcNoches = (entrada, salida) => {
  if (!entrada || !salida) return 0;
  const d1 = new Date(entrada + 'T12:00:00');
  const d2 = new Date(salida + 'T12:00:00');
  return Math.max(0, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)));
};

const ReservaDetalleModal = ({ isOpen, onClose, reserva, onConfirmar, onCancelar, onUpdated }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');

  const { data: habData } = useQuery(GET_HABITACIONES, {
    skip: !editMode,
  });

  const [actualizarReserva, { loading: saving }] = useMutation(ACTUALIZAR_RESERVA);

  if (!reserva) return null;

  const habitaciones = (habData?.habitaciones || []).filter(h => h.activa && h.estado !== 'mantenimiento');
  const esEditable = reserva.estado === 'pendiente' || reserva.estado === 'confirmada';

  // Edit mode helpers
  // Grado militar del huésped de la reserva
  const gradoHuesped = reserva.huesped?.grado_militar || null;

  const handleEdit = () => {
    setFormData({
      habitacion_id: reserva.habitacion_id,
      fecha_entrada: toDateInput(reserva.fecha_entrada),
      fecha_salida: toDateInput(reserva.fecha_salida),
      num_adultos: reserva.num_adultos || 1,
      num_ninos: reserva.num_ninos || 0,
      precio_noche: reserva.precio_noche || 0,
      precio_mensual: reserva.precio_mensual || 0,
      tipo_tarifa: reserva.tipo_tarifa || 'mensual',
      anticipo: reserva.anticipo || 0,
      observaciones: reserva.observaciones || '',
      notas_especiales: reserva.notas_especiales || '',
    });
    setError('');
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : parseFloat(value)) : value,
    }));
  };

  const handleTipoTarifaChange = (nuevoTipo) => {
    let nuevoPrecioNoche = formData.precio_noche;
    let nuevoPrecioMensual = formData.precio_mensual;
    if (gradoHuesped) {
      if (nuevoTipo === 'temporal') {
        nuevoPrecioNoche = parseFloat(gradoHuesped.precio_noche_temporal) || (parseFloat(gradoHuesped.precio_noche) / 30);
        nuevoPrecioMensual = 0;
      } else {
        nuevoPrecioMensual = parseFloat(gradoHuesped.precio_noche); // tarifa mensual completa
        nuevoPrecioNoche = nuevoPrecioMensual / 30;                  // per-night para display
      }
    }
    // Revalidar anticipo contra el nuevo total
    const noches = calcNoches(formData.fecha_entrada, formData.fecha_salida);
    const nuevoTotal = nuevoTipo === 'mensual'
      ? Math.ceil(noches / 30) * (nuevoPrecioMensual || nuevoPrecioNoche * 30)
      : noches * nuevoPrecioNoche;
    const anticipoAjustado = Math.min(formData.anticipo || 0, nuevoTotal);
    setFormData(prev => ({
      ...prev,
      tipo_tarifa: nuevoTipo,
      precio_noche: nuevoPrecioNoche,
      precio_mensual: nuevoPrecioMensual,
      anticipo: anticipoAjustado,
    }));
  };

  const handleSave = async () => {
    setError('');
    const noches = calcNoches(formData.fecha_entrada, formData.fecha_salida);
    if (noches <= 0) {
      setError('La fecha de salida debe ser posterior a la fecha de entrada');
      return;
    }
    try {
      await actualizarReserva({
        variables: {
          id: parseInt(reserva.id),
          input: {
            habitacion_id: parseInt(formData.habitacion_id),
            fecha_entrada: formData.fecha_entrada,
            fecha_salida: formData.fecha_salida,
            num_adultos: parseInt(formData.num_adultos) || 1,
            num_ninos: parseInt(formData.num_ninos) || 0,
            tipo_tarifa: formData.tipo_tarifa || 'mensual',
            anticipo: parseFloat(formData.anticipo) || 0,
            observaciones: formData.observaciones || null,
            notas_especiales: formData.notas_especiales || null,
          },
        },
      });
      setEditMode(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.message || 'Error al actualizar la reserva');
    }
  };

  // Computed values for edit mode
  const editNoches = editMode ? calcNoches(formData.fecha_entrada, formData.fecha_salida) : 0;
  const editTotal = editMode
    ? (formData.tipo_tarifa === 'mensual'
      ? Math.ceil(editNoches / 30) * ((formData.precio_mensual || 0) > 0 ? formData.precio_mensual : (formData.precio_noche || 0) * 30)
      : editNoches * (formData.precio_noche || 0))
    : 0;
  const editSaldo = editMode ? editTotal - (formData.anticipo || 0) : 0;

  // Configuracion de badges segun estado
  const estadoConfig = {
    pendiente: { label: 'Pendiente', color: '#f59e0b', icon: AlertCircle },
    confirmada: { label: 'Confirmada', color: '#3b82f6', icon: CheckCircle },
    en_curso: { label: 'En Curso', color: '#10b981', icon: CheckCircle },
    finalizada: { label: 'Finalizada', color: '#6b7280', icon: CheckCircle },
    cancelada: { label: 'Cancelada', color: '#ef4444', icon: XCircle },
    no_show: { label: 'No Show', color: '#ef4444', icon: XCircle },
  };

  const estadoInfo = estadoConfig[reserva.estado] || estadoConfig.pendiente;
  const EstadoIcon = estadoInfo.icon;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const str = typeof dateString === 'string' ? dateString : dateString.toString();
    const dateStr = str.includes('T') ? str : str + 'T12:00:00';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-CO', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const noches = reserva.noches || 0;
  const mostrarBotones = reserva.estado === 'pendiente' || reserva.estado === 'confirmada';
  const mostrarConfirmar = reserva.estado === 'pendiente';
  const mostrarCancelar = reserva.estado === 'pendiente' || reserva.estado === 'confirmada';

  const handleClose = () => {
    setEditMode(false);
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editMode ? 'Editar Reserva' : 'Detalles de la Reserva'}
      size="large"
    >
      <div className="reserva-detalle">
        {/* Header con codigo y estado */}
        <div className="reserva-detalle__header">
          <div className="reserva-detalle__codigo">
            <span className="codigo-label">Codigo de Reserva</span>
            <span className="codigo-value">{reserva.codigo}</span>
          </div>
          <div className="reserva-detalle__header-right">
            {esEditable && !editMode && (
              <button className="reserva-detalle__edit-btn" onClick={handleEdit} title="Editar reserva">
                <Edit3 size={16} />
                Editar
              </button>
            )}
            <div
              className="reserva-detalle__badge"
              style={{ border: `2px solid ${estadoInfo.color}` }}
            >
              <EstadoIcon size={18} style={{ color: estadoInfo.color }} />
              <span style={{ color: estadoInfo.color }}>{estadoInfo.label}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="reserva-detalle__error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Seccion: Informacion del Huesped (siempre read-only) */}
        <div className="reserva-detalle__section">
          <h3 className="section-title">
            <User size={18} />
            Informacion del Huesped
          </h3>
          <div className="section-content">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Nombre Completo</span>
                <span className="info-value">{reserva.huesped?.nombre_completo || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Documento</span>
                <span className="info-value">
                  {reserva.huesped?.tipo_documento}: {reserva.huesped?.numero_documento || 'N/A'}
                </span>
              </div>
              {reserva.huesped?.telefono && (
                <div className="info-item">
                  <Phone size={14} className="info-icon" />
                  <span className="info-label">Telefono</span>
                  <span className="info-value">{reserva.huesped.telefono}</span>
                </div>
              )}
              {reserva.huesped?.email && (
                <div className="info-item">
                  <Mail size={14} className="info-icon" />
                  <span className="info-label">Email</span>
                  <span className="info-value">{reserva.huesped.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Seccion: Informacion de la Habitacion */}
        <div className="reserva-detalle__section">
          <h3 className="section-title">
            <Home size={18} />
            Informacion de la Habitacion
          </h3>
          <div className="section-content">
            {editMode ? (
              <div className="edit-field">
                <label className="edit-label">Habitacion</label>
                <select
                  name="habitacion_id"
                  value={formData.habitacion_id}
                  onChange={handleChange}
                  className="edit-select"
                >
                  {/* Keep current room as option even if not in list */}
                  {!habitaciones.find(h => h.id == formData.habitacion_id) && reserva.habitacion && (
                    <option value={reserva.habitacion_id}>
                      {reserva.habitacion.numero} - {reserva.habitacion.tipo} (actual)
                    </option>
                  )}
                  {habitaciones.map(h => (
                    <option key={h.id} value={h.id}>
                      {h.numero} - {h.tipo} (Piso {h.piso}) - {h.capacidad} cama(s)
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Numero de Habitacion</span>
                  <span className="info-value info-value--highlight">
                    {reserva.habitacion?.numero || 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Tipo</span>
                  <span className="info-value">{reserva.habitacion?.tipo || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Piso</span>
                  <span className="info-value">{reserva.habitacion?.piso || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Estado Actual</span>
                  <span className="info-value">{reserva.habitacion?.estado || 'N/A'}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Seccion: Fechas y Estadia */}
        <div className="reserva-detalle__section">
          <h3 className="section-title">
            <Calendar size={18} />
            Fechas y Estadia
          </h3>
          <div className="section-content">
            {editMode ? (
              <div className="edit-grid">
                <div className="edit-field">
                  <label className="edit-label">Fecha de Entrada</label>
                  <input
                    type="date"
                    name="fecha_entrada"
                    value={formData.fecha_entrada}
                    onChange={handleChange}
                    className="edit-input"
                  />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Fecha de Salida</label>
                  <input
                    type="date"
                    name="fecha_salida"
                    value={formData.fecha_salida}
                    onChange={handleChange}
                    className="edit-input"
                  />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Estadia</label>
                  <div className="edit-computed">
                    {editNoches} {editNoches === 1 ? 'noche' : 'noches'}
                    {formData.tipo_tarifa === 'mensual' && editNoches > 0 && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85em', color: 'var(--hotel-text-secondary, #6b7280)' }}>
                        ({Math.ceil(editNoches / 30)} {Math.ceil(editNoches / 30) === 1 ? 'mes' : 'meses'})
                      </span>
                    )}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Adultos</label>
                  <input
                    type="number"
                    name="num_adultos"
                    value={formData.num_adultos}
                    onChange={handleChange}
                    min="1"
                    className="edit-input"
                  />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Ninos</label>
                  <input
                    type="number"
                    name="num_ninos"
                    value={formData.num_ninos}
                    onChange={handleChange}
                    min="0"
                    className="edit-input"
                  />
                </div>
              </div>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Fecha de Entrada</span>
                  <span className="info-value">{formatDate(reserva.fecha_entrada)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Fecha de Salida</span>
                  <span className="info-value">{formatDate(reserva.fecha_salida)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Numero de Noches</span>
                  <span className="info-value info-value--highlight">
                    {noches} {noches === 1 ? 'noche' : 'noches'}
                  </span>
                </div>
                <div className="info-item">
                  <Users size={14} className="info-icon" />
                  <span className="info-label">Huespedes</span>
                  <span className="info-value">
                    {reserva.num_adultos || 0} adultos, {reserva.num_ninos || 0} ninos
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Seccion: Informacion Financiera */}
        <div className="reserva-detalle__section">
          <h3 className="section-title">
            <CreditCard size={18} />
            Informacion Financiera
          </h3>
          <div className="section-content">
            {editMode ? (
              <div className="edit-grid">
                <div className="edit-field">
                  <label className="edit-label">Tipo de Tarifa</label>
                  <div style={{ display: 'flex', gap: '1rem', padding: '6px 0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                      <input
                        type="radio"
                        name="tipo_tarifa"
                        value="mensual"
                        checked={formData.tipo_tarifa === 'mensual'}
                        onChange={() => handleTipoTarifaChange('mensual')}
                      />
                      Mensual{gradoHuesped ? ` (${formatCurrency(gradoHuesped.precio_noche)}/mes)` : ''}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                      <input
                        type="radio"
                        name="tipo_tarifa"
                        value="temporal"
                        checked={formData.tipo_tarifa === 'temporal'}
                        onChange={() => handleTipoTarifaChange('temporal')}
                      />
                      Temporal{gradoHuesped ? ` (${formatCurrency(gradoHuesped.precio_noche_temporal || 0)}/noche)` : ''}
                    </label>
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">
                    {formData.tipo_tarifa === 'mensual' ? 'Precio por Mes' : 'Precio por Noche'}
                  </label>
                  <div className="edit-computed">
                    {formatCurrency(formData.tipo_tarifa === 'mensual'
                      ? ((formData.precio_mensual || 0) > 0 ? formData.precio_mensual : (formData.precio_noche || 0) * 30)
                      : formData.precio_noche)}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Anticipo (COP)</label>
                  <input
                    type="number"
                    name="anticipo"
                    value={formData.anticipo}
                    onChange={handleChange}
                    min="0"
                    className="edit-input"
                  />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Precio Total</label>
                  <div className="edit-computed edit-computed--large">{formatCurrency(editTotal)}</div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Saldo Pendiente</label>
                  <div className="edit-computed edit-computed--large" style={{ color: editSaldo > 0 ? '#f59e0b' : '#10b981' }}>
                    {formatCurrency(editSaldo)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="info-grid info-grid--financial">
                <div className="info-item">
                  <span className="info-label">Tipo de Tarifa</span>
                  <span className="info-value" style={{ textTransform: 'capitalize' }}>
                    {reserva.tipo_tarifa || 'mensual'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    {(reserva.tipo_tarifa || 'mensual') === 'mensual' ? 'Precio por Mes' : 'Precio por Noche'}
                  </span>
                  <span className="info-value">
                    {formatCurrency(
                      (reserva.tipo_tarifa || 'mensual') === 'mensual'
                        ? (reserva.precio_mensual || reserva.precio_noche * 30)
                        : reserva.precio_noche
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Precio Total</span>
                  <span className="info-value info-value--large">{formatCurrency(reserva.precio_total)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Anticipo</span>
                  <span className="info-value" style={{ color: '#10b981' }}>
                    {formatCurrency(reserva.anticipo)}
                  </span>
                </div>
                <div className="info-item info-item--highlight">
                  <span className="info-label">Saldo Pendiente</span>
                  <span className="info-value info-value--large" style={{ color: '#f59e0b' }}>
                    {formatCurrency(reserva.saldo_pendiente)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Seccion: Informacion Adicional */}
        <div className="reserva-detalle__section">
          <h3 className="section-title">
            <FileText size={18} />
            Informacion Adicional
          </h3>
          <div className="section-content">
            {editMode ? (
              <div className="edit-grid edit-grid--single">
                <div className="edit-field edit-field--full">
                  <label className="edit-label">Observaciones</label>
                  <textarea
                    name="observaciones"
                    value={formData.observaciones}
                    onChange={handleChange}
                    className="edit-textarea"
                    rows={3}
                    placeholder="Observaciones de la reserva..."
                  />
                </div>
                <div className="edit-field edit-field--full">
                  <label className="edit-label">Notas Especiales</label>
                  <textarea
                    name="notas_especiales"
                    value={formData.notas_especiales}
                    onChange={handleChange}
                    className="edit-textarea"
                    rows={3}
                    placeholder="Notas especiales (alergias, preferencias, etc.)..."
                  />
                </div>
              </div>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <MapPin size={14} className="info-icon" />
                  <span className="info-label">Canal de Reserva</span>
                  <span className="info-value">{reserva.canal_reserva || 'directo'}</span>
                </div>
                {reserva.observaciones && (
                  <div className="info-item info-item--full">
                    <span className="info-label">Observaciones</span>
                    <span className="info-value">{reserva.observaciones}</span>
                  </div>
                )}
                {reserva.notas_especiales && (
                  <div className="info-item info-item--full">
                    <span className="info-label">Notas Especiales</span>
                    <span className="info-value info-value--notes">{reserva.notas_especiales}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Seccion: Registro de Fechas (siempre read-only) */}
        {!editMode && (
          <div className="reserva-detalle__section">
            <h3 className="section-title">
              <Clock size={18} />
              Registro de Fechas
            </h3>
            <div className="section-content">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Fecha de Creacion</span>
                  <span className="info-value">{formatDateTime(reserva.created_at)}</span>
                </div>
                {reserva.confirmed_at && (
                  <div className="info-item">
                    <span className="info-label">Fecha de Confirmacion</span>
                    <span className="info-value" style={{ color: '#3b82f6' }}>
                      {formatDateTime(reserva.confirmed_at)}
                    </span>
                  </div>
                )}
                {reserva.cancelled_at && (
                  <div className="info-item">
                    <span className="info-label">Fecha de Cancelacion</span>
                    <span className="info-value" style={{ color: '#ef4444' }}>
                      {formatDateTime(reserva.cancelled_at)}
                    </span>
                  </div>
                )}
                {reserva.motivo_cancelacion && (
                  <div className="info-item info-item--full">
                    <span className="info-label">Motivo de Cancelacion</span>
                    <span className="info-value" style={{ color: '#ef4444' }}>
                      {reserva.motivo_cancelacion}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Botones de accion */}
        {editMode ? (
          <div className="reserva-detalle__actions">
            <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
              <X size={16} />
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              <Save size={16} />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        ) : mostrarBotones ? (
          <div className="reserva-detalle__actions">
            <Button variant="outline" onClick={handleClose}>
              Cerrar
            </Button>
            {mostrarCancelar && onCancelar && (
              <Button
                variant="danger"
                onClick={() => onCancelar(reserva)}
              >
                <XCircle size={18} />
                Cancelar Reserva
              </Button>
            )}
            {mostrarConfirmar && onConfirmar && (
              <Button
                variant="primary"
                onClick={() => onConfirmar(reserva)}
              >
                <CheckCircle size={18} />
                Confirmar Reserva
              </Button>
            )}
          </div>
        ) : (
          <div className="reserva-detalle__actions">
            <Button variant="outline" onClick={handleClose}>
              Cerrar
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReservaDetalleModal;
